import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { api, apiUrl } from '../lib/api';
import Button from '../components/Button';

interface LicenseStatus {
  license: {
    id: string;
    status: string;
    bound: boolean;
    resets_this_month: number;
    max_resets_per_month: number;
    purchased_at: string | null;
  } | null;
}

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  show: (i: number) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.4, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] as const },
  }),
};

export default function Dashboard() {
  const { t } = useTranslation();
  const { user, license, loading } = useAuth();
  const [status, setStatus] = useState<LicenseStatus['license']>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [resetLoading, setResetLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => { loadStatus(); }, []);

  const loadStatus = async () => {
    try {
      const data = await api.get<LicenseStatus>('/license/status');
      setStatus(data.license);
    } catch { setStatus(null); }
    finally { setStatusLoading(false); }
  };

  const handleDownload = () => {
    setDownloadLoading(true);
    setMessage('');
    window.location.href = apiUrl('/download');
    setTimeout(() => setDownloadLoading(false), 3000);
  };

  const handleReset = async () => {
    if (!confirm(t('dashboard.resetConfirm'))) return;
    setResetLoading(true);
    setMessage('');
    try {
      await api.post('/license/reset');
      await loadStatus();
      setMessage(t('dashboard.resetSuccess'));
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Reset failed');
    } finally { setResetLoading(false); }
  };

  if (loading || statusLoading) {
    return <div className="max-w-4xl mx-auto px-6 py-20"><div className="animate-pulse text-muted">{t('dashboard.loading')}</div></div>;
  }

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        <h1 className="font-display text-2xl font-bold mb-4">{t('dashboard.notSignedIn')}</h1>
        <p className="text-muted mb-6">{t('dashboard.notSignedInDesc')}</p>
        <Link to="/login" className="text-primary hover:text-primary-hover">{t('dashboard.goToLogin')}</Link>
      </div>
    );
  }

  const currentLicense = status ?? license;
  const hasLicense = currentLicense && currentLicense.status !== 'inactive';

  return (
    <div className="max-w-4xl mx-auto px-6 py-20">
      <motion.h1
        className="font-display text-3xl font-bold mb-2"
        initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}
      >
        {t('dashboard.title')}
      </motion.h1>
      <p className="text-muted text-sm mb-10">{user.email}</p>

      {!hasLicense && (
        <motion.div
          className="bg-surface border border-border rounded-lg p-8 text-center"
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
        >
          <h2 className="font-display text-xl font-semibold mb-2">{t('dashboard.noLicense')}</h2>
          <p className="text-muted text-sm mb-6">{t('dashboard.noLicenseDesc')}</p>
          <Link to="/pricing" className="inline-block px-6 py-3 rounded bg-primary hover:bg-primary-hover text-white font-medium transition-colors">
            {t('dashboard.purchase')}
          </Link>
        </motion.div>
      )}

      {hasLicense && currentLicense && (
        <div className="space-y-6">
          <motion.div custom={0} variants={cardVariants} initial="hidden" animate="show" className="bg-surface border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-semibold">{t('dashboard.licenseStatus')}</h2>
              <StatusBadge status={currentLicense.status} />
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted text-xs uppercase tracking-wide block mb-1">{t('dashboard.licenseId')}</span>
                <code className="text-xs break-all">{currentLicense.id}</code>
              </div>
              <div>
                <span className="text-muted text-xs uppercase tracking-wide block mb-1">{t('dashboard.hwidBound')}</span>
                <span>{currentLicense.bound ? t('dashboard.yes') : t('dashboard.notActivated')}</span>
              </div>
              <div>
                <span className="text-muted text-xs uppercase tracking-wide block mb-1">{t('dashboard.resetsThisMonth')}</span>
                <span>{currentLicense.resets_this_month ?? 0} / {currentLicense.max_resets_per_month ?? 2}</span>
              </div>
              <div>
                <span className="text-muted text-xs uppercase tracking-wide block mb-1">{t('dashboard.purchased')}</span>
                <span>{currentLicense.purchased_at ? new Date(currentLicense.purchased_at).toLocaleDateString() : '—'}</span>
              </div>
            </div>
          </motion.div>

          <motion.div custom={1} variants={cardVariants} initial="hidden" animate="show" className="bg-surface border border-border rounded-lg p-6">
            <h2 className="font-display text-lg font-semibold mb-4">{t('dashboard.download')}</h2>
            <p className="text-sm text-muted mb-4">{t('dashboard.downloadDesc')}</p>
            <Button onClick={handleDownload} disabled={downloadLoading || currentLicense.status !== 'active'}>
              {downloadLoading ? t('dashboard.preparing') : t('dashboard.downloadLoader')}
            </Button>
          </motion.div>

          <motion.div custom={2} variants={cardVariants} initial="hidden" animate="show" className="bg-surface border border-border rounded-lg p-6">
            <h2 className="font-display text-lg font-semibold mb-4">{t('dashboard.hwidReset')}</h2>
            <p className="text-sm text-muted mb-4">{t('dashboard.hwidResetDesc', { max: currentLicense.max_resets_per_month ?? 2 })}</p>
            <Button variant="ghost" onClick={handleReset} disabled={resetLoading}>
              {resetLoading ? t('dashboard.resetting') : t('dashboard.resetHwid')}
            </Button>
          </motion.div>

          {message && <div className="bg-primary/10 border border-primary/30 rounded px-4 py-3 text-sm">{message}</div>}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    active: 'bg-success/10 text-success border-success/30',
    inactive: 'bg-muted/10 text-muted border-border',
    disabled: 'bg-danger/10 text-danger border-danger/30',
    chargeback: 'bg-danger/10 text-danger border-danger/30',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border ${styles[status] ?? styles.inactive}`}>
      {status === 'active' && (
        <span className="relative flex h-1.5 w-1.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75" />
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-success" />
        </span>
      )}
      <span className="uppercase tracking-wide">{status}</span>
    </span>
  );
}
